import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TakeOffSourceLogo from "./TakeOffSourceLogo";
import { BodyText } from "../../../shared/text/BodyText.styled";
import { DROPDOWN_OPTION_SX } from "../../../styles/dropdown";
import { TakeOffSourceType } from "../../../context/DatasourceContext";
import { useApiSourceSessions } from "../../../hooks/useQtoSources";
import { useBanner } from "../../../context/BannerContext";
import { ediphiPalette } from "../../../styles/colors";
import { signedOutOf } from "../../../constants";

interface TakeOffSourceHeaderProps {
  type: TakeOffSourceType;
  /** Shown as "Logged in as …". Omitted by sources that expose no signed-in identity. */
  email?: string;
}

/**
 * The header of an authenticated take-off source panel: the source logo, who is signed in, and a
 * menu whose only item signs them out. Render it only where the source is already authenticated —
 * it makes no claim about auth state itself.
 */
const TakeOffSourceHeader: React.FC<TakeOffSourceHeaderProps> = ({ type, email }) => {
  const { showBanner } = useBanner();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // File-backed sources have no session to end, so they get the logo without the menu.
  const entry = useApiSourceSessions()[type];

  const handleLogout = async () => {
    setMenuAnchor(null);
    if (!entry) return;
    await entry.session.logout();
    showBanner(signedOutOf(entry.source.label), "success");
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <TakeOffSourceLogo type={type} variant="drawer" />
        {entry && (
          <IconButton
            size="small"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            aria-label={`${entry.source.label} options`}
            sx={{ color: ediphiPalette.greyText, flexShrink: 0 }}
          >
            <MoreVertIcon sx={{ fontSize: "18px" }} />
          </IconButton>
        )}
      </Box>
      {email && <BodyText noWrap>{`Logged in as ${email}`}</BodyText>}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => void handleLogout()} sx={DROPDOWN_OPTION_SX}>
          Log out
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TakeOffSourceHeader;
